/**
 * The Planner's Overdue card: incomplete past-due todos, oldest first. The
 * screen owns the filter/sort; this component presents the list and owns how
 * rows LEAVE it — and how they come back. Ticking a row commits the toggle at
 * once (the store drops the todo optimistically), but the row does not blink
 * out with it: a snapshot stays in its slot showing the checkmark, holds a
 * beat, then collapses so the rows below glide up. Once the last row has gone
 * the card collapses the same way and only then unmounts — the screen never
 * jumps. The way back mirrors the way out: a todo that becomes overdue again
 * (an un-check from the day list, or a toggle that rolled back) grows into its
 * slot, and a card that had gone opens the way it closed. The card also owns
 * the gap below it (`gapBelow`) for that reason: it has to go — and come —
 * WITH the card, not a frame later when the parent's flex gap notices.
 *
 * The header folds the rows the same way: they shrink away and unmount, or
 * mount clipped and grow back, while the chevron turns rather than swapping
 * glyphs. The card and the rows block share one `Fold`.
 *
 * Always mounted by the screen; renders nothing when it has nothing to show.
 * Reduced motion keeps the instant behavior. Expanded by default — surfacing
 * stragglers is the section's whole job.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  View,
  StyleSheet,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useMotion } from '@/hooks/shared/useMotion';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { formatDayHeading, parseDateKey } from '@/utils/residents/todoCalendar';
import type { ResidentTodo } from '@/types/residents/todos';
import { TodoRow } from './TodoRow';

interface OverdueSectionProps {
  /** Already filtered (incomplete, past-due) and sorted oldest-first. */
  todos: ResidentTodo[];
  onToggle: (id: string) => void;
  onEdit: (todo: ResidentTodo) => void;
  /** Space below the card. Lives inside the collapse so it leaves with it. */
  gapBelow?: number;
}

export interface DisplayRow {
  todo: ResidentTodo;
  /** Gone from `todos`; kept only until its exit animation finishes. */
  leaving: boolean;
  /** Not among the previous rows; grows into its slot on mount. */
  entering: boolean;
}

/**
 * Rows for the next render: `todos` in its own order plus — when keeping
 * them — any row that just vanished, left in its old slot and flagged so it
 * can animate out in place. A flagged row that is back in `todos` is simply a
 * present row again (fresh object, flag cleared). With `flagNew`, a todo that
 * was not among the previous rows is marked entering so it can grow in.
 */
export const reconcileRows = (
  prev: DisplayRow[],
  todos: ResidentTodo[],
  keepVanished: boolean,
  flagNew = false,
): DisplayRow[] => {
  const prevIds = new Set(prev.map(r => r.todo.id));
  const next: DisplayRow[] = todos.map(todo => ({
    todo,
    leaving: false,
    entering: flagNew && !prevIds.has(todo.id),
  }));
  if (!keepVanished) return next;
  const present = new Set(todos.map(t => t.id));
  prev.forEach((row, index) => {
    if (present.has(row.todo.id)) return;
    next.splice(Math.min(index, next.length), 0, {
      todo: row.todo,
      leaving: true,
      entering: false,
    });
  });
  return next;
};

/** `ids` minus `drop`; the same instance when nothing changes, so a
 *  render-phase setState with the result bails out. */
const without = (
  ids: ReadonlySet<string>,
  drop: Iterable<string>,
): ReadonlySet<string> => {
  let next: Set<string> | null = null;
  for (const id of drop) {
    if (!ids.has(id)) continue;
    next ??= new Set(ids);
    next.delete(id);
  }
  return next ?? ids;
};

/**
 * How far a leaving row drifts up while it fades, and how far below its slot
 * an arriving row settles up from (AnimatedEntrance's 8).
 */
const EXIT_RISE = 8;

const layoutStyles = StyleSheet.create({
  clip: { overflow: 'hidden' },
  // Nothing on screen while an arriving element waits for its measurement.
  unmeasured: { height: 0, opacity: 0 },
});

type FoldPhase = 'closed' | 'opening' | 'open' | 'closing';

interface FoldProps {
  /** Whether the content should be up. Flipping it starts the transition. */
  open: boolean;
  /** False under reduced motion: mount or unmount at once. */
  animate: boolean;
  /** Style for the measured inner view (the card keeps its gap there). */
  innerStyle?: StyleProp<ViewStyle>;
  children: React.ReactNode;
}

/**
 * Mounts `children` and grows them in when `open`; shrinks them away and
 * unmounts them when not. Same shape as a row's exit: the outer view is what
 * animates (height is a layout prop, so this runs on the JS driver — it is
 * what makes everything below move with it); the inner view keeps the natural
 * height and is where that height gets measured. Opening content is clipped
 * to nothing until its first measurement lands, then grows over it, re-aimed
 * if the content grows meanwhile. A flip mid-way turns around from wherever
 * the value is instead of jumping.
 */
const Fold: React.FC<FoldProps> = ({ open, animate, innerStyle, children }) => {
  const motion = useMotion();
  // Synced during render, not in an effect, so the frame that flips `open` is
  // the frame the fold reacts — no in-between commit.
  const [phase, setPhase] = useState<FoldPhase>(open ? 'open' : 'closed');
  if (open && (phase === 'closed' || phase === 'closing')) {
    setPhase(animate ? 'opening' : 'open');
  } else if (!open && (phase === 'open' || phase === 'opening')) {
    setPhase(animate ? 'closing' : 'closed');
  }

  const progress = useRef(new Animated.Value(open ? 1 : 0)).current;
  const heightRef = useRef<number | null>(null);
  // The height the outer view is animating over; null = natural layout (or
  // not yet measured while opening).
  const [height, setHeight] = useState<number | null>(null);
  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const measured = e.nativeEvent.layout.height;
      heightRef.current = measured;
      // Opening content follows its own growth: a row can still land mid-open.
      if (phase === 'opening') setHeight(measured);
    },
    [phase],
  );

  // Closing shrinks over the height measured as it starts. Open and closed
  // park the value at their end, so the next transition starts from it.
  useEffect(() => {
    if (phase === 'closed') {
      progress.setValue(0);
      setHeight(null);
      return;
    }
    if (phase === 'open') {
      progress.setValue(1);
      setHeight(null);
      return;
    }
    if (phase !== 'closing') return;
    setHeight(heightRef.current);
    const close = Animated.timing(progress, {
      toValue: 0,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    close.start(({ finished }) => {
      if (finished) setPhase('closed');
    });
    return () => close.stop();
  }, [phase, progress, motion]);

  // Opening grows to the measured height from wherever the value is — 0 after
  // closed, part-way after an interrupted close.
  useEffect(() => {
    if (phase !== 'opening' || height === null) return;
    const grow = Animated.timing(progress, {
      toValue: 1,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    grow.start(({ finished }) => {
      if (finished) setPhase('open');
    });
    return () => grow.stop();
  }, [phase, height, progress, motion]);

  if (phase === 'closed') return null;

  const style =
    phase === 'open'
      ? undefined
      : height !== null
        ? [
            layoutStyles.clip,
            {
              height: progress.interpolate({ inputRange: [0, 1], outputRange: [0, height] }),
              opacity: progress,
            },
          ]
        : phase === 'opening'
          ? [layoutStyles.clip, layoutStyles.unmeasured]
          : { opacity: progress }; // closing before any measurement: fade only

  return (
    <Animated.View style={style}>
      <View onLayout={handleLayout} style={innerStyle}>
        {children}
      </View>
    </Animated.View>
  );
};

interface OverdueRowProps {
  todo: ResidentTodo;
  isLast: boolean;
  /** Ticked here: draw the checkmark and hold a beat before collapsing. */
  checked: boolean;
  leaving: boolean;
  /** Mounted for a todo that just became overdue again: grow in. */
  entering: boolean;
  onToggle: (id: string) => void;
  onEdit: (todo: ResidentTodo) => void;
  onExited: (id: string) => void;
}

/**
 * One row plus its exit and its entrance. The outer view is what shrinks or
 * grows (height is a layout prop, so this runs on the JS driver — fine for a
 * handful of rows, and it is what makes the rows below move with it); the
 * inner view keeps the natural height and is where that height gets measured.
 * An arriving row is clipped to nothing until that first measurement lands.
 */
const OverdueRow: React.FC<OverdueRowProps> = ({
  todo,
  isLast,
  checked,
  leaving,
  entering,
  onToggle,
  onEdit,
  onExited,
}) => {
  const motion = useMotion();
  // Decided at mount: a row only ever grows in from nothing.
  const growIn = useRef(entering).current;
  const progress = useRef(new Animated.Value(growIn ? 0 : 1)).current;
  const heightRef = useRef<number | null>(null);
  // The height the outer view is animating over; null = natural layout.
  const [animHeight, setAnimHeight] = useState<number | null>(null);
  // False until an arriving row has grown to full size.
  const [landed, setLanded] = useState(!growIn);
  const wasLeaving = useRef(false);

  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { height } = e.nativeEvent.layout;
      heightRef.current = height;
      // An arriving row can grow once it knows how tall it is.
      if (!landed && animHeight === null) setAnimHeight(height);
    },
    [landed, animHeight],
  );

  // Exit: pin the outer view to the natural height and shrink it away. Only
  // the flip to `leaving` starts one, so a re-render mid-exit can't restart it.
  useEffect(() => {
    const justLeft = leaving && !wasLeaving.current;
    wasLeaving.current = leaving;
    if (!justLeft) return;
    setAnimHeight(heightRef.current);
    const exit = Animated.sequence([
      // A tapped row shows its checkmark for a beat before it goes; a row that
      // left for another reason (edited to a later date) just goes.
      Animated.delay(checked ? motion.duration.base : 0),
      Animated.timing(progress, {
        toValue: 0,
        duration: motion.duration.base,
        easing: Easing.bezier(...motion.easing.standard),
        useNativeDriver: false,
      }),
    ]);
    exit.start(({ finished }) => {
      if (finished) onExited(todo.id);
    });
    return () => exit.stop();
    // `checked` is read as the exit starts; a later change must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving, motion, progress, onExited, todo.id]);

  // Settle: a pinned row that is not leaving grows to full size — an arrival
  // once measured, or a rolled-back toggle turning its collapse around — then
  // hands its slot back to natural layout.
  useEffect(() => {
    if (leaving || animHeight === null) return;
    const settle = Animated.timing(progress, {
      toValue: 1,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    settle.start(({ finished }) => {
      if (!finished) return;
      setAnimHeight(null);
      setLanded(true);
    });
    return () => settle.stop();
  }, [leaving, animHeight, motion, progress]);

  // The store has already dropped a ticked todo, so the snapshot has to be
  // told to draw the checkmark; a row that is present always shows live state.
  const shownTodo = leaving && checked ? { ...todo, completed: true } : todo;
  // A row on its way out drifts up; one on its way in settles up from below.
  const yFrom = landed ? -EXIT_RISE : EXIT_RISE;
  const animStyle =
    animHeight !== null
      ? [
          layoutStyles.clip,
          {
            height: progress.interpolate({ inputRange: [0, 1], outputRange: [0, animHeight] }),
            opacity: progress,
            transform: [
              {
                translateY: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [yFrom, 0],
                }),
              },
            ],
          },
        ]
      : landed
        ? undefined
        : [layoutStyles.clip, layoutStyles.unmeasured];

  return (
    <Animated.View style={animStyle}>
      <View onLayout={handleLayout}>
        <TodoRow
          todo={shownTodo}
          isLast={isLast}
          onToggle={onToggle}
          onEdit={onEdit}
          dateLabel={formatDayHeading(parseDateKey(todo.dueDate))}
        />
      </View>
    </Animated.View>
  );
};

export const OverdueSection: React.FC<OverdueSectionProps> = ({
  todos,
  onToggle,
  onEdit,
  gapBelow = 0,
}) => {
  const [expanded, setExpanded] = useState(true);
  const type = useTypography();
  const space = useSpacing();
  const motion = useMotion();
  const reduceMotion = useReducedMotion();
  const animate = !reduceMotion;

  // Rows lag `todos` so a vanished row can finish leaving. Synced during
  // render, not in an effect, so the frame that drops a todo is the frame
  // that flags its row — no in-between commit.
  const [syncedTodos, setSyncedTodos] = useState(todos);
  const [rows, setRows] = useState<DisplayRow[]>(() => reconcileRows([], todos, false));
  // Ids ticked here, so their snapshot rows know to draw the checkmark.
  const [checkedIds, setCheckedIds] = useState<ReadonlySet<string>>(() => new Set());
  if (syncedTodos !== todos) {
    setSyncedTodos(todos);
    // Rows behind the fold can't animate, so they just go — or just appear.
    // A newcomer grows in on its own only when it joins rows that are
    // already up; a card that is opening (or turning around from a close,
    // with only leaving rows left) carries it in with its own growth.
    const live = animate && expanded;
    setRows(reconcileRows(rows, todos, live, live && rows.some(r => !r.leaving)));
    // A ticked todo that is back in the list means its toggle rolled back.
    setCheckedIds(without(checkedIds, todos.map(t => t.id)));
  }
  const hasRows = rows.length > 0;

  // The chevron turns with the fold: down while expanded, right while folded.
  // JS-driven like the fold itself, so the two stay in step. Only a flip of
  // `expanded` turns it: a timing to the value already held would still run
  // its full duration, leaking a frame loop from every mount for nothing.
  const chevron = useRef(new Animated.Value(expanded ? 1 : 0)).current;
  const wasExpanded = useRef(expanded);
  useEffect(() => {
    const target = expanded ? 1 : 0;
    const flipped = wasExpanded.current !== expanded;
    wasExpanded.current = expanded;
    if (!animate || !flipped) {
      chevron.setValue(target);
      return;
    }
    const turn = Animated.timing(chevron, {
      toValue: target,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    turn.start();
    return () => turn.stop();
  }, [expanded, animate, chevron, motion]);

  const handleToggle = useCallback(
    (id: string) => {
      if (animate) setCheckedIds(prev => new Set(prev).add(id));
      onToggle(id);
    },
    [animate, onToggle],
  );
  const handleRowExited = useCallback((id: string) => {
    setRows(prev => prev.filter(r => r.todo.id !== id));
    setCheckedIds(prev => without(prev, [id]));
  }, []);
  const handleHeaderPress = useCallback(() => {
    setExpanded(e => !e);
    // Rows behind the fold can't finish an exit, or an entrance: drop any exit
    // in flight, and clear entering flags so a row comes back whole on unfold
    // (a row reads its flag once, at mount, so a landed one is cleared here too).
    const inFlight = rows.filter(r => r.leaving);
    if (inFlight.length > 0 || rows.some(r => r.entering)) {
      setRows(
        rows.filter(r => !r.leaving).map(r => (r.entering ? { ...r, entering: false } : r)),
      );
      setCheckedIds(prev => without(prev, inFlight.map(r => r.todo.id)));
    }
  }, [rows]);

  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.sm,
        minHeight: 44,
      },
      warnIcon: {
        color: semantic.status.warning,
      },
      headerText: {
        ...type.cardTitle,
        color: colors.text.primary,
        flex: 1,
      },
      chevron: {
        color: colors.text.muted,
      },
    }),
  );

  // Remaining work while there is any; otherwise whatever is still on its way
  // out. Never "· 0": by then the card is closing, not showing an empty list.
  const shownCount = todos.length || rows.length || 1;
  const a11yLabel = `Overdue, ${shownCount} ${shownCount === 1 ? 'todo' : 'todos'}`;

  return (
    <Fold
      open={hasRows}
      animate={animate}
      innerStyle={gapBelow > 0 ? { paddingBottom: gapBelow } : undefined}
    >
      <GlassCard>
        <Hoverable
          hover="row"
          style={styles.headerRow}
          onPress={handleHeaderPress}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          accessibilityLabel={a11yLabel}
        >
          <Icon name="alert-circle-outline" size={18} style={styles.warnIcon} />
          <Text style={styles.headerText}>{`Overdue · ${shownCount}`}</Text>
          <Animated.View
            style={{
              transform: [
                {
                  rotate: chevron.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['-90deg', '0deg'],
                  }),
                },
              ],
            }}
          >
            <Icon name="chevron-down" size={16} style={styles.chevron} />
          </Animated.View>
        </Hoverable>
        <Fold open={expanded} animate={animate}>
          <View>
            {rows.map((row, index) => (
                <OverdueRow
                  key={row.todo.id}
                  todo={row.todo}
                  isLast={index === rows.length - 1}
                  checked={checkedIds.has(row.todo.id)}
                  leaving={row.leaving}
                  entering={row.entering}
                  onToggle={handleToggle}
                  onEdit={onEdit}
                  onExited={handleRowExited}
                />
              ))}
          </View>
        </Fold>
      </GlassCard>
    </Fold>
  );
};

OverdueSection.displayName = 'OverdueSection';
